import SkeletonTable from "@/components/SkeletonTable"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import api from "@/lib/axios"
import { DataNilaiRapor } from "@/types"
import { useEffect, useState } from "react"
import { FaArrowLeftLong, FaFaceGrinBeam } from "react-icons/fa6"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

type DataPendaftaran = {
  id_data_pendaftaran: string
  nomor_pendaftaran: string
  nisn: string
  nama_lengkap: string
  handphone: string
  npsn_sekolah: string
  nama_sekolah: string
  pilihan1: string
  pilihan2: string
}

const getDetailNilaiRaport = async ({
  token,
  nomor_pendaftaran,
}: {
  token: string
  nomor_pendaftaran: string
}) => {
  const response = await api.get(
    `/nilai-rapor-smbjm-public?token=${token}&nomor_pendaftaran=${nomor_pendaftaran}`
  )
  return response.data?.data
}

export default function DetailNilaiRaportSmbjmPublicPage() {
  const [searchParams] = useSearchParams()
  const tokenParams = searchParams.get("token") || ""
  const nomorPendaftaranParams = searchParams.get("nomor_pendaftaran") || ""

  const [form] = useState({
    nilai_ver1: "",
    validasi_ver1: 0,
    ket_ver1: "",
    nilai_ver2: "",
    validasi_ver2: 0,
    ket_ver2: "",
    nilai_ver3: "",
    validasi_ver3: 0,
    ket_ver3: "",
  })

  const verifikatorKe = 1
  const keyNilai = `nilai_ver${verifikatorKe}` as keyof typeof form
  const keyKet = `ket_ver${verifikatorKe}` as keyof typeof form
  const keyValidasi = `validasi_ver${verifikatorKe}` as keyof typeof form
  const keyNamaVerifikator = `nama_ver${verifikatorKe}` as keyof typeof form

  const [selectedNilaiRapor, setSelectedNilaiRapor] =
    useState<DataNilaiRapor | null>(null)

  const { data, isPending } = useQuery<{
    dataPendaftaran: DataPendaftaran
    dataNilaiRapor: DataNilaiRapor[]
    verifikatorKe: string | number
  }>({
    queryKey: ["detailNilaiRaport"],
    queryFn: () =>
      getDetailNilaiRaport({
        token: tokenParams,
        nomor_pendaftaran: nomorPendaftaranParams,
      }),
    enabled: !!tokenParams && !!nomorPendaftaranParams,
  })

  useEffect(() => {
    if (!data) return
    setSelectedNilaiRapor(data?.dataNilaiRapor[0])
  }, [data])

  if (isPending) return <SkeletonTable />

  if (!data)
    return (
      <div className="h-screen flex justify-center items-center">
        <h1 className="font-bold text-2xl">Data tidak ditemukan!</h1>
      </div>
    )

  return (
    <div>
      <div className="relative p-5 flex lg:flex-row flex-col gap-3 h-[90vh] overflow-auto">
        <section className="p-5 lg:w-[40%] border rounded-xl">
          <div className="mb-10">
            <table>
              <thead>
                <tr>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-2 py-1 font-semibold">Nomor Pendaftaran</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.dataPendaftaran?.nomor_pendaftaran}
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">Nama</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.dataPendaftaran?.nama_lengkap}
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">Prodi Pilihan</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    <ul>
                      <li>
                        {data?.dataPendaftaran?.pilihan1
                          ? `1. ${data?.dataPendaftaran?.pilihan1}`
                          : ""}
                      </li>
                      <li>
                        {data?.dataPendaftaran?.pilihan2
                          ? `2. ${data?.dataPendaftaran?.pilihan2}`
                          : ""}
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">NISN</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">{data?.dataPendaftaran?.nisn}</td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">No Handphone</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.dataPendaftaran?.handphone}
                  </td>
                </tr>
                <tr>
                  <td className="px-2 py-1 font-semibold">Sekolah</td>
                  <td className="px-2 py-1">:</td>
                  <td className="px-2 py-1">
                    {data?.dataPendaftaran?.nama_sekolah} |{" "}
                    {data?.dataPendaftaran?.npsn_sekolah}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex-1 overflow-auto border rounded-xl">
            <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-secondary">
                  <th className="px-2 py-1 font-semibold border" rowSpan={2}>
                    No
                  </th>
                  <th className="px-2 py-1 font-semibold border" rowSpan={2}>
                    Berkas
                  </th>
                  <th className="px-2 py-1 font-semibold border">Peserta</th>
                  <th className="px-2 py-1 font-semibold border" colSpan={3}>
                    Verifikator
                  </th>
                  <th
                    className="px-2 py-1 font-semibold border"
                    rowSpan={2}
                  ></th>
                </tr>
                <tr>
                  <th className="px-2 py-1 font-semibold border">
                    Nilai Rata2
                  </th>
                  <th className="px-2 py-1 font-semibold border">
                    Nilai Rata2
                  </th>
                  <th className="px-2 py-1 font-semibold border">Status</th>
                  <th className="px-2 py-1 font-semibold border">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {data && data?.dataNilaiRapor.length > 0
                  ? data?.dataNilaiRapor.map(
                      (item: DataNilaiRapor, index: number) => {
                        const isActive =
                          item.id_data_nilai_rapor ==
                          selectedNilaiRapor?.id_data_nilai_rapor

                        return (
                          <tr key={item.id_data_nilai_rapor}>
                            <td className="px-2 py-1 border text-center">
                              {index + 1}
                            </td>
                            <td className="px-2 py-1 border text-center">
                              Semester {item.semester}
                            </td>
                            <td className="px-2 py-1 border text-center">
                              {item.rata_rata_nilai}
                            </td>
                            <td className="px-2 py-1 border text-center">
                              {item[keyNilai as keyof DataNilaiRapor]}
                            </td>
                            <td className="px-2 py-1 border text-center">
                              {(() => {
                                const value =
                                  item[keyValidasi as keyof DataNilaiRapor]

                                if (value === 0)
                                  return (
                                    <Badge variant={"secondary"}>
                                      Belum diverifikasi
                                    </Badge>
                                  )
                                if (value === 1)
                                  return (
                                    <Badge variant={"success"}>Valid</Badge>
                                  )
                                if (value === 2)
                                  return (
                                    <Badge variant={"destructive"}>
                                      Tidak Valid
                                    </Badge>
                                  )

                                return ""
                              })()}
                              <p className="text-sm italic">
                                {item[keyNamaVerifikator]}
                              </p>
                            </td>
                            <td className="px-2 py-1 border text-center">
                              {item[keyKet]}
                            </td>
                            <td className="px-2 py-1 border text-center">
                              <Button
                                variant={isActive ? "dark" : "outline"}
                                onClick={() => {
                                  setSelectedNilaiRapor(item)
                                }}
                                className="w-full"
                              >
                                {isActive ? "Sedang dilihat" : "Lihat"}
                              </Button>
                            </td>
                          </tr>
                        )
                      }
                    )
                  : "Data nilai rapor tidak ditemukan..."}
              </tbody>
            </table>
          </div>
        </section>
        <section className="relative overflow-auto flex-1 border rounded-xl">
          <div className="p-5 pb-0 flex items-center gap-5">
            {selectedNilaiRapor ? (
              <div className="flex-1 text-xl font-semibold">
                <div className="flex-1 flex flex-wrap justify-between font-bold text-xl">
                  <div>
                    Berkas Rapor{" "}
                    <span className="text-primary">
                      Semester {selectedNilaiRapor?.semester}
                    </span>{" "}
                    <span>
                      Nilai Rata-Rata{" "}
                      <Badge className="text-xl">
                        {selectedNilaiRapor?.rata_rata_nilai}
                      </Badge>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
          {selectedNilaiRapor ? (
            <div className="relative h-[80vh]">
              <iframe
                src={selectedNilaiRapor?.file_rapor_semester || ""}
                className="p-5 absolute w-full h-full"
              />
            </div>
          ) : (
            <div className="h-[60vh] flex justify-center items-center">
              <div>
                <FaFaceGrinBeam size={50} className="mb-5 mx-auto" />
                <div className="text-2xl font-bold flex items-center gap-3">
                  <FaArrowLeftLong />
                  Pilih berkas untuk dilihat
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
